import { useState, useEffect } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Users, Brain, Database, FolderKanban, Landmark, AlertTriangle, Activity, RefreshCw, Shield, DollarSign, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PolicyEditor } from '../components/policy/PolicyEditor';
import { UsageDashboard } from '../components/dashboard/UsageDashboard';
import KillSwitchCard from '../components/dashboard/KillSwitchCard';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, icon: Icon, color, bg }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
          <div className={`p-2 rounded-lg ${bg}`}><Icon className={`w-4 h-4 ${color}`} /></div>
        </div>
        <span className="text-3xl font-bold tracking-tight">{value}</span>
      </CardContent>
    </Card>
  );
}

function SyncStatusBadge({ status }) {
  if (status === 'ok') return <Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]">OK</Badge>;
  if (status === 'error') return <Badge variant="destructive" className="text-[11px]">Erreur</Badge>;
  return <Badge variant="secondary" className="text-[11px]">Jamais</Badge>;
}

function formatDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}

export default function PlatformDashboardPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [docsHealth, setDocsHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, docsRes] = await Promise.all([
        axios.get(`${API}/platform/dashboard`, { headers }),
        axios.get(`${API}/platform/docs-health`, { headers }).catch(() => ({ data: null })),
      ]);
      setData(dashRes.data);
      setDocsHealth(docsRes.data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const c = data?.counters || {};

  return (
    <PlatformLayout title="Dashboard">
      <div className="space-y-6" data-testid="platform-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Vue d'ensemble de la plateforme Clone IA</p>
          <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={loading} data-testid="refresh-dashboard">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="platform-stats-grid">
          <StatCard label="Tenants" value={c.tenants_total ?? 0} icon={Building2} color="text-blue-500" bg="bg-blue-500/10" />
          <StatCard label="Utilisateurs" value={c.users_total ?? 0} icon={Users} color="text-violet-500" bg="bg-violet-500/10" />
          <StatCard label="Clones" value={c.clones_total ?? 0} icon={Brain} color="text-indigo-500" bg="bg-indigo-500/10" />
          <StatCard label="Memories" value={c.memories_total ?? 0} icon={Database} color="text-rose-500" bg="bg-rose-500/10" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Tenants actifs" value={c.tenants_active ?? 0} icon={Activity} color="text-emerald-500" bg="bg-emerald-500/10" />
          <StatCard label="Dossiers" value={c.dossiers_total ?? 0} icon={FolderKanban} color="text-amber-500" bg="bg-amber-500/10" />
          <StatCard label="Organisations" value={c.organizations_total ?? 0} icon={Landmark} color="text-teal-500" bg="bg-teal-500/10" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source Alerts */}
          <Card data-testid="source-alerts-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Alertes sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!data?.source_alerts || data.source_alerts.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucune alerte</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {data.source_alerts.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 rounded-md bg-destructive/5 border border-destructive/15 text-sm">
                      <Badge variant="outline" className="text-[10px] shrink-0 mt-0.5">{a.type}</Badge>
                      <div className="min-w-0">
                        <p className="text-xs text-destructive font-medium truncate">{a.error}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Tenant: {a.tenant_id || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Syncs */}
          <Card data-testid="recent-syncs-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-blue-500" /> Dernières synchronisations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!data?.recent_syncs || data.recent_syncs.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Aucune synchro</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Tenant</TableHead>
                      <TableHead className="text-xs">Dernière synchro</TableHead>
                      <TableHead className="text-xs">Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recent_syncs.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell><Badge variant="outline" className="text-[10px]">{s.type}</Badge></TableCell>
                        <TableCell className="text-xs">{s.tenant_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(s.last_sync)}</TableCell>
                        <TableCell><SyncStatusBadge status={s.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Logins */}
        <Card data-testid="recent-logins-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-500" /> Dernières connexions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data?.recent_logins || data.recent_logins.length === 0) ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Aucune connexion recente</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Utilisateur</TableHead>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Tenant</TableHead>
                    <TableHead className="text-xs">Role</TableHead>
                    <TableHead className="text-xs">Derniere connexion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent_logins.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-medium">{l.first_name} {l.last_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{l.email}</TableCell>
                      <TableCell className="text-xs">{l.tenant_name || '-'}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{l.role}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(l.last_login)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* LLM Usage / Billing */}
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-2 mb-3">
            <DollarSign className="w-4 h-4 text-emerald-500" /> Consommation LLM
          </h2>
          <div className="mb-3">
            <KillSwitchCard token={token} />
          </div>
          <UsageDashboard token={token} days={30} />
        </div>

        {/* Global Clone Policy */}
        <PolicyEditor
          title="Politique Globale des Clones"
          description="Ces règles s'appliquent à TOUS les clones de la plateforme."
          icon={Shield}
          fetchUrl="/platform/global-policy"
          addUrl="/platform/global-policy/rules"
          updateUrl="/platform/global-policy/rules"
          deleteUrl="/platform/global-policy/rules"
          token={token}
        />

        {/* Docs Health Widget */}
        {docsHealth && (
          <Card
            className="cursor-pointer hover:border-primary/50 transition-colors"
            data-testid="docs-health-widget"
            onClick={() => navigate('/platform/docs')}
          >
            <CardContent className="flex items-center gap-4 pt-5 pb-4">
              <div className="p-2.5 rounded-lg bg-indigo-500/10">
                <BookOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">Documentation technique</p>
                <p className="text-xs text-muted-foreground">
                  {docsHealth.existing_docs}/{docsHealth.total_docs} fichiers
                  {' '}&middot;{' '}{docsHealth.total_lines?.toLocaleString('fr-FR')} lignes
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-muted-foreground">Dernière MAJ</p>
                <p className="text-sm font-medium">
                  {docsHealth.newest_update
                    ? new Date(docsHealth.newest_update).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '-'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PlatformLayout>
  );
}
